import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calculator, Loader2, RotateCcw, Save, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CanvasStage from '@/components/canvas/CanvasStage';
import { useAutoScale } from '@/components/canvas/hooks/useAutoScale';
import { usePolygonEditor } from '@/hooks/canvas/usePolygonEditor';
import { stageSizePx } from '@/utils/canvasGutter';
import { listAreasByCamera } from '@/api/areas';
import { getLatestFrame } from '@/api/frames';
import { toAbsoluteUrl } from '@/api/url';
import { API_HOST } from '@/api/base';
import type { CheckoutZoneData } from '@/app/appContext';

// Полигон очереди прямо в плашке калибровки (Алексей 21.10): тот же редактор, что на канвасе
// «Зонирования» (usePolygonEditor + CanvasStage — вершины, точка по клику на ребро, рамка,
// шаг назад), и то же сохранение (PATCH PolygonArea.direction_vectors). На холсте — только
// полигон этой очереди на последнем кадре камеры. «Пересчитать по черновику» ничего не
// сохраняет: гистограмма плашки пересчитывается по боксам под черновой полигон.

interface AreaPts { id: string; points: [number, number][] }

export default function QueuePolygonEditor({ czId, cameraId, name, areaIds, onPreview, onSaved, onDiscard }: {
  /** CheckoutZone id (как у строки калибровки). */
  czId: string;
  cameraId: string;
  name: string;
  /** PolygonArea группы очереди; на холсте — те, что на этой камере. */
  areaIds: string[];
  /** Черновик {area_id: точки} — пересчитать гистограмму без сохранения. */
  onPreview: (draft: Record<string, number[][]>) => void;
  /** Полигон сохранён — гистограмму пересчитать по сохранённому. */
  onSaved: () => void;
  /** Правки отменены — убрать черновой пересчёт. */
  onDiscard: () => void;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [areas, setAreas] = useState<AreaPts[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const failedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const editor = usePolygonEditor({ onError: (m) => { failedRef.current = true; setError(m); } });
  const areaKey = areaIds.join(',');

  const loadAreas = useCallback(async () => {
    const wanted = new Set(areaKey.split(',').filter(Boolean));
    const r = await listAreasByCamera(cameraId);
    setAreas(r.data.results
      .filter((a) => wanted.has(String(a.id)))
      .map((a) => ({ id: String(a.id), points: (a.direction_vectors ?? []) as [number, number][] })));
  }, [cameraId, areaKey]);

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    loadAreas().catch(() => { if (alive) setLoadError('Не удалось загрузить полигон.'); });
    getLatestFrame(cameraId).then((r) => {
      if (!alive || !r.data?.image) { if (alive) setLoadError('У камеры нет свежего кадра.'); return; }
      const img = new window.Image();
      img.onload = () => { if (alive) setImage(img); };
      img.onerror = () => { if (alive) setLoadError('Кадр камеры не загрузился.'); };
      img.src = toAbsoluteUrl(r.data.image, API_HOST || undefined);
    }).catch(() => { if (alive) setLoadError('У камеры нет свежего кадра.'); });
    return () => { alive = false; };
  }, [cameraId, loadAreas]);

  const area = areas?.[0] ?? null;
  const cz: CheckoutZoneData = useMemo(() => ({
    id: czId, group: '', name, type: 'QUEUE', areas: (areas ?? []).map((a) => a.id),
  }), [czId, name, areas]);
  const getAreaPoints = useCallback((id: string) => areas?.find((a) => a.id === id)?.points ?? [], [areas]);

  // Редактирование включено всегда, пока открыт спойлер: после отмены — заново с сохранёнными
  // точками; после сохранения редактор сбрасывается раньше, чем перечитан полигон, поэтому
  // перечитанный полигон без несохранённых правок тоже перезапускает редактирование.
  const { editingPolygons, startEditing, isDirty } = editor;
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  useEffect(() => {
    if (image && area && area.points.length >= 3 && (editingPolygons.length === 0 || !dirtyRef.current)) {
      startEditing(czId, 'queue', area.points, false);
    }
    // Из editingPolygons — только переход в «не редактируется»; правки точек сюда не ходят.
  }, [image, area, editingPolygons.length === 0, startEditing, czId]);

  const editing = editingPolygons[0];
  const draft = useMemo(() => (area && editing ? { [area.id]: editing.points.map((p) => [p[0], p[1]]) } : null), [area, editing]);
  const scale = useAutoScale(containerRef, image ? stageSizePx({ width: image.width, height: image.height }) : null);

  const save = async () => {
    setError(null);
    failedRef.current = false;
    await editor.handleSaveEdit([], [], [], [cz], cameraId);
    if (failedRef.current) return;
    try { await loadAreas(); } catch { /* перечитается при следующем открытии */ }
    onSaved();
  };
  const discard = () => {
    editor.cancelEditing();
    onDiscard();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && editor.selectedPoints.length > 0) {
      e.preventDefault();
      editor.deleteSelectedPoints();
    } else if ((e.key === 'z' || e.key === 'я') && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
      e.preventDefault();
      editor.undo();
    } else if (e.key === 'Escape') {
      editor.clearPointSelection();
    }
  };

  if (loadError) return <div className="text-xs text-destructive">{loadError}</div>;
  if (areas && !area) {
    return <div className="text-xs text-muted-foreground">Полигона очереди на этой камере нет — правится в «Зонировании».</div>;
  }
  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="relative flex h-[440px] w-full items-center justify-center overflow-hidden rounded-md bg-[#1f1f1f] outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {image && areas ? (
          <CanvasStage
            image={image}
            scale={scale}
            mode="view"
            selectedPolygon={czId}
            onStageClick={() => {}}
            editingPolygons={editingPolygons}
            selectedPoints={editor.selectedPoints}
            onPointSelect={editor.handlePointSelect}
            onPointDragStart={editor.handlePointDragStart}
            onPointDrag={editor.handlePointDrag}
            onPolygonDragStart={editor.handlePolygonDragStart}
            onPolygonDrag={editor.handlePolygonDrag}
            onPointDragEnd={editor.handlePointDragEnd}
            onPolygonClick={(_id, _type, extend, pos) => { if (!extend && pos) editor.handleEdgeClick(pos); }}
            onEdgeClick={editor.handleEdgeClick}
            tempPoints={[]}
            selectedRackForShelf={null}
            awaitingRackSelection={false}
            onRackClickForShelf={() => {}}
            standaloneAreas={[]}
            filteredRacks={[]}
            filteredShelves={[]}
            filteredTables={[]}
            filteredCheckoutZones={[cz]}
            getAreaPoints={getAreaPoints}
            onSelectPointsInRect={editor.handleSelectPointsInRect}
            onClearPointSelection={editor.clearPointSelection}
          />
        ) : (
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="mr-auto text-muted-foreground">
          Тяните вершины; клик по ребру — новая точка; рамкой — выделить несколько, Del — удалить, Ctrl+Z — шаг назад. Как в «Зонировании».
        </span>
        <Button type="button" size="sm" variant="outline" disabled={editor.saving || editor.selectedPoints.length === 0} onClick={editor.deleteSelectedPoints}>
          <Trash2 className="size-3.5" />Удалить точки
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={editor.saving || !editor.isDirty} onClick={editor.undo}>
          <Undo2 className="size-3.5" />Шаг назад
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={editor.saving || !editor.isDirty} onClick={discard}>
          <RotateCcw className="size-3.5" />Отменить правки
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={editor.saving || !editor.isDirty || !draft} onClick={() => draft && onPreview(draft)}
          title="Пересчитать гистограмму и фото по черновику — ничего не сохраняется">
          <Calculator className="size-3.5" />Пересчитать по черновику
        </Button>
        <Button type="button" size="sm" variant="default" disabled={editor.saving || !editor.isDirty} onClick={() => { void save(); }}>
          {editor.saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}Сохранить
        </Button>
      </div>
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
}
